import React, { useEffect, useRef, useState } from 'react';
import { Document, PDFViewer, Font } from '@react-pdf/renderer';
import fontRegular from 'src/assets/fonts/Poppins-Regular.ttf';
import fontMedium from 'src/assets/fonts/Poppins-Medium.ttf';
import fontBold from 'src/assets/fonts/Poppins-Bold.ttf';
import _ from 'lodash';
import { HtmlWrap, SnapChartImageV2 } from '../comps';
import { ActionsConf, Toc, TocItem } from '../types';
import {
  AssessmentSummary,
  AssessmentEnd,
  ScenRender,
  AssessmentHead,
  AssessmentToc,
  AssessmentExecSummary,
  RmpRender,
  IncidentsRender,
} from './comps';
import { getMinMax, parseRiskTolerance, plotLineGradient } from 'src/components/charts/util';
import { auditEvent } from 'src/api/other';
import { IS_DEBUG } from '../conf';
import { ChartConf } from 'src/components/charts/Chart/Chart';
import { ColorSet } from 'src/redux/reducers/theme';
import { useAssessmentPdfQuery } from 'src/graphql/_generated';
import { useAuth } from 'src/state/auth';

Font.registerHyphenationCallback((word) => [word]);

Font.register({
  family: 'Poppins',
  fonts: [
    {
      src: fontRegular,
      fontWeight: 400,
    },
    {
      src: fontMedium,
      fontWeight: 500,
    },
    {
      src: fontBold,
      fontWeight: 600,
    },
  ],
});

interface PdfAssessmentProps {
  assessmentId: string;
  isLogarithmic: boolean;
  renderScenarios?: string[];
  onClose: () => void;
  includesExecSummary: boolean;
  includesRMP: boolean;
  includesIncidents: boolean;
  includesScenarios: boolean;
  includesRiskTolerance: boolean;
  colors: ColorSet;
  includesActions: ActionsConf;
}

const PdfAssessment: React.FC<PdfAssessmentProps> = ({
  assessmentId,
  isLogarithmic,
  renderScenarios = [],
  onClose,
  includesExecSummary,
  includesRMP,
  includesIncidents,
  includesScenarios,
  includesRiskTolerance,
  colors,
  includesActions,
}) => {
  const { profile } = useAuth();

  const [tableOfContents, setTableOfContents] = useState<{
    hasExecResult: boolean;
    hasAssessmentResult: boolean;
    hasScenarioResult: boolean;
    hasRmpResult: boolean;
    hasIncidentsResult: boolean;
    contents: Toc[];
  }>({
    hasExecResult: !includesExecSummary,
    hasAssessmentResult: false,
    hasScenarioResult: !renderScenarios.length,
    hasRmpResult: !includesRMP,
    hasIncidentsResult: !includesIncidents,
    contents: [],
  });

  const [charts, setCharts] = useState<{
    hasChartsResults: boolean;
    assessmentChart: string;
    scenarioCharts: string[];
  }>({ hasChartsResults: false, assessmentChart: '', scenarioCharts: [] });

  const { data: gqlData } = useAssessmentPdfQuery({
    variables: {
      id: assessmentId,
    },
  });

  const renderCount = useRef(0);

  useEffect(() => {
    if (!gqlData?.assessment) return;

    const contents: Toc[] = [];
    if (includesExecSummary) {
      contents.push({ title: 'Executive Summary', pageNumber: -1, id: 'exec.summary' });
    }
    contents.push({ title: 'Assessment Summary', pageNumber: -1, id: gqlData.assessment.name.toLowerCase() });
    if (includesScenarios && renderScenarios.length) {
      contents.push({ title: 'Scenarios', pageNumber: -1 });
    }
    if (includesRMP) {
      contents.push({ title: 'Risk Management Plan', pageNumber: -1, id: 'rmp.plan' });
    }
    if (includesIncidents) {
      contents.push({ title: 'Incidents', pageNumber: -1, id: 'incidents' });
    }
    setTableOfContents((s) => {
      return { ...s, contents };
    });
  }, [includesExecSummary, renderScenarios, includesRMP, gqlData?.assessment]);

  const assessment = gqlData?.assessment;
  const filteredScenarios =
    assessment?.scenarios?.filter((el) => renderScenarios.length === 0 || renderScenarios.includes(el.id)) || [];

  const controls = includesRMP
    ? _.uniqBy(
        filteredScenarios.flatMap(
          (scenario) => scenario.riskManagementPlans?.map((rmp) => rmp.control).filter(Boolean) || [],
        ),
        'id',
      )
    : [];

  const incidents = includesIncidents
    ? _.uniqBy(
        filteredScenarios.flatMap((scenario) => scenario.incidents || []),
        'id',
      )
    : [];

  const onExecDone = async (pageNumber: number) => {
    if (!tableOfContents.hasExecResult) {
      const contents = _.cloneDeep(tableOfContents?.contents) as Toc[];
      const idx = contents.findIndex((el) => el.title === 'Executive Summary');
      contents[idx].pageNumber = pageNumber;

      setTableOfContents((s) => {
        return { ...s, hasExecResult: true, contents };
      });
    }
  };

  const onAssessmentDone = (pageNumber: number) => {
    if (tableOfContents.hasExecResult && !tableOfContents.hasAssessmentResult) {
      const contents = _.cloneDeep(tableOfContents.contents) as Toc[];
      const idx = contents.findIndex((el) => el.title === 'Assessment Summary');
      contents[idx].pageNumber = pageNumber;

      setTableOfContents((s) => {
        return { ...s, hasAssessmentResult: true, contents };
      });
    }
  };

  const onScenariosDone = async (data: TocItem[]) => {
    if (tableOfContents.hasExecResult && tableOfContents.hasAssessmentResult && !tableOfContents.hasScenarioResult) {
      const contents = _.cloneDeep(tableOfContents?.contents) as Toc[];
      const idx = contents.findIndex((el) => el.title === 'Scenarios');
      contents[idx].children = data;
      contents[idx].pageNumber = data[0].pageNumber;
      contents[idx].id = data[0].title.toLowerCase();

      setTableOfContents((s) => {
        return { ...s, hasScenarioResult: true, contents };
      });
    }
  };

  const onRmpDone = (pageNumber: number, data: TocItem[]) => {
    if (
      tableOfContents.hasExecResult &&
      tableOfContents.hasAssessmentResult &&
      tableOfContents.hasScenarioResult &&
      !tableOfContents.hasRmpResult
    ) {
      const contents = _.cloneDeep(tableOfContents.contents) as Toc[];
      const idx = contents.findIndex((el) => el.title === 'Risk Management Plan');
      contents[idx].pageNumber = pageNumber;
      contents[idx].children = data;

      setTableOfContents((s) => {
        return { ...s, hasRmpResult: true, contents };
      });
    }
  };

  const onIncidentsDone = (pageNumber: number, data: TocItem[]) => {
    if (
      tableOfContents.hasExecResult &&
      tableOfContents.hasAssessmentResult &&
      tableOfContents.hasScenarioResult &&
      tableOfContents.hasRmpResult &&
      !tableOfContents.hasIncidentsResult
    ) {
      const contents = _.cloneDeep(tableOfContents.contents) as Toc[];
      const idx = contents.findIndex((el) => el.title === 'Incidents');
      contents[idx].pageNumber = pageNumber;
      contents[idx].children = data;

      setTableOfContents((s) => {
        return { ...s, hasIncidentsResult: true, contents };
      });
    }
  };

  // FILE DOWNLOAD
  const print = (blob: Blob | undefined) => {
    if (blob) {
      let elm = document.createElement('a');
      elm.href = URL.createObjectURL(blob);
      elm.setAttribute('download', `${assessment?.name || 'Assessment'}.pdf`);
      elm.click();
      elm.remove();

      auditEvent({
        action: 'ExportPdf',
        resource: 'assessment',
        assessment_id: assessmentId,
      });

      onClose();
    }
  };

  if (!assessment) return null;

  const showAllProjected = !!filteredScenarios
    ?.flatMap((scenario) => scenario.liveRisk?.expectedLoss)
    .filter((el) => !!el).length;

  const scenariosForMinMax = filteredScenarios.map((scenario) => ({
    upper: scenario.liveRisk?.upper || scenario.baseRisk?.upper || 0,
    lower: scenario.liveRisk?.lower || scenario.baseRisk?.lower || 0,
  }));

  const { upper: maxUpper, lower: maxLower } = getMinMax(scenariosForMinMax);

  const assessmentCharts: ChartConf[] = [
    {
      data: assessment.AssessmentLiveRisk?.chart || [],
      upper: maxUpper,
      lower: maxLower,
      color: plotLineGradient,
      fillColor: 'rgba(230, 46, 97, 0.07)',
      name: 'live chart',
    },
    ...(showAllProjected
      ? [
          {
            data: assessment.AssessmentBaseRisk?.chart || [],
            upper: maxUpper,
            lower: maxLower,
            color: colors.prussianBlue,
            fillColor: 'rgba(2, 3, 51, 0.07)',
            name: 'chart',
          },
          {
            data: assessment.AssessmentProjectedRisk?.chart || [],
            upper: maxUpper,
            lower: maxLower,
            color: colors.brightBlue,
            fillColor: 'rgba(100, 149, 237, 0.07)',
            name: 'completechart',
          },
        ]
      : []),
  ];

  if (includesRiskTolerance) {
    assessmentCharts.push({
      data: parseRiskTolerance(assessment.riskTolerance || ''),
      name: 'rt',
      type: 'spline',
      dashStyle: 'ShortDash',
      fillColor: colors.cflowerBlue,
      color: colors.cflowerBlue,
      isRiskTolerance: true,
    });
  }

  return (
    <HtmlWrap $isTest={IS_DEBUG}>
      <SnapChartImageV2
        disabled={charts.hasChartsResults}
        isLogarithmic={isLogarithmic}
        chartConf={[
          {
            charts: assessmentCharts,
          },
        ]}
        onSnap={(img) =>
          setCharts((s) => {
            return { ...s, assessmentChart: img[0], hasChartsResults: !filteredScenarios.length };
          })
        }
        colors={colors}
      />

      <SnapChartImageV2
        disabled={charts.hasChartsResults || !filteredScenarios.length}
        isLogarithmic={isLogarithmic}
        chartConf={filteredScenarios.map((el) => {
          const showAllProjected = !!el.liveRisk?.expectedLoss;
          return {
            charts: [
              {
                data: el.liveRisk?.chart || [],
                upper: el.liveRisk?.upper || 0,
                lower: el.liveRisk?.lower || 0,
                color: plotLineGradient,
                fillColor: 'rgba(230, 46, 97, 0.07)',
                name: 'live chart',
              },
              ...(showAllProjected
                ? [
                    {
                      data: el.baseRisk?.chart || [],
                      upper: el.baseRisk?.upper || 0,
                      lower: el.baseRisk?.lower || 0,
                      color: colors.prussianBlue,
                      fillColor: 'rgba(2, 3, 51, 0.07)',
                      name: 'chart',
                    },
                    {
                      data: el.projectedRisk?.chart || [],
                      upper: el.projectedRisk?.upper || 0,
                      lower: el.projectedRisk?.lower || 0,
                      color: colors.brightBlue,
                      fillColor: 'rgba(100, 149, 237, 0.07)',
                      name: 'complete chart',
                    },
                  ]
                : []),
            ],
          };
        })}
        onSnap={(img) =>
          setCharts((s) => {
            return {
              ...s,
              scenarioCharts: img,
              hasChartsResults: true,
            };
          })
        }
        colors={colors}
      />
      {charts.hasChartsResults && (
        <PDFViewer>
          <Document
            onRender={(el) => {
              let requiredRenders = 1;

              if (includesExecSummary) {
                requiredRenders += 1;
              }
              if (renderScenarios.length > 0) {
                requiredRenders += 1;
              }
              if (includesRMP) {
                requiredRenders += 1;
              }
              if (includesIncidents) {
                requiredRenders += 1;
              }

              if (renderCount.current === requiredRenders) {
                if (!IS_DEBUG) {
                  print(el.blob);
                }
              } else {
                renderCount.current += 1;
              }
            }}
          >
            <AssessmentHead
              assessment={gqlData?.assessment!}
              colors={colors}
              companyName={profile?.activeWorkspace?.name || ''}
              exportedBy={profile?.user?.orgUnitIdentity?.name || ''}
            />
            <AssessmentToc contents={tableOfContents.contents} colors={colors} />
            {includesExecSummary && (
              <AssessmentExecSummary
                onDone={onExecDone}
                execSummary={assessment.executiveSummary || ''}
                colors={colors}
              />
            )}

            <AssessmentSummary
              onDone={onAssessmentDone}
              assessment={assessment}
              scenarios={filteredScenarios}
              chart={charts.assessmentChart}
              includesRMP={includesRMP}
              includesRiskTolerance={includesRiskTolerance}
              colors={colors}
              showAllProjected={showAllProjected}
              includesActions={includesActions}
            />

            {renderScenarios.length && tableOfContents.hasAssessmentResult && (
              <ScenRender
                scenarios={filteredScenarios.map((el, idx) => ({
                  ...el,
                  chartImage: charts.scenarioCharts[idx] || '',
                }))}
                onDone={onScenariosDone}
                assessmentData={{
                  reviewDate: assessment.reviewDate || undefined,
                  name: assessment.name,
                  id: assessment.id,
                }}
                colors={colors}
                includesActions={includesActions}
              />
            )}
            {includesRMP && (
              <RmpRender
                onDone={onRmpDone}
                assessment={assessment}
                controls={controls.filter((c): c is NonNullable<typeof c> => Boolean(c))}
                colors={colors}
                scenarios={filteredScenarios}
                includesActions={includesActions}
              />
            )}
            {includesIncidents && (
              <IncidentsRender
                onDone={onIncidentsDone}
                assessment={assessment}
                incidents={incidents}
                colors={colors}
                includesActions={includesActions}
              />
            )}
            <AssessmentEnd assessment={assessment} colors={colors} />
          </Document>
        </PDFViewer>
      )}
    </HtmlWrap>
  );
};

export default PdfAssessment;
